<template>
    <div>
        <h2 class="text-primary-500" v-if="!reports">
            Assignment Status Report
        </h2>

        <div class="flex flex-wrap">
            <div v-if="!reports">
                <d-field label="Search by assignee name" class="w-full mb-5">

                    <div class="pt-2 relative mx-auto text-black">
                        <d-autocomplete v-model="assigneeNameEmail"
                                        :data="users"
                                        item-key="email"
                                        icon="search"
                                        placeholder="Type to find a user..."
                                        field="email"
                                        @typing="getUserDataAsync">
                            <template v-slot="{option}">
                                <p>{{ option.fullName }}</p>
                                <p class="text-sm text-gray-600">
                                    {{ option.email }}
                                </p>
                            </template>
                            <template slot="empty">
                                No results found
                            </template>
                        </d-autocomplete>
                    </div>
                </d-field>

                <d-field label="Search by Group/Community">
                    <d-autocomplete v-model="searchGroupValue"
                                    open-on-focus
                                    :loading="isFetchingGroups"
                                    :data="availableGroups"
                                    placeholder="Select a group..."
                                    field="name"
                                    position="top"
                                    @typing="attemptGetGroups"
                                    @input="val => addToGroupArray(val)">
                        <template v-slot="{option}">
                            <p>{{ option.name }}</p>
                            <p class="text-sm text-gray-600">
                                Group/Community
                            </p>
                        </template>
                        <template slot="empty">
                            No results found
                        </template>
                    </d-autocomplete>
                </d-field>

                <div class="w-full mt-5">
                    <d-btn :loading="fetchingReport" @click="handleSubmit">
                        Generate Report
                    </d-btn>
                </div>
            </div>
            <div v-else>
                <a href="#" @click="reports=null"><u>Search again</u></a><br />
                <a target="_blank" :href="'/reports/assignment-status/print?assigneeNameOrEmail='+this.assigneeNameEmail+'&groupId='+this.searchGroupValue"><u>Print</u></a>
                <br />

                <div v-for="reportData in reports">

                    <h2 class="text-primary-500">
                        Assignment Status Report: {{ reportData.assigneeName }}
                    </h2>
                    <d-table class="mt-4" :data="reportData.activeAssignments" striped detail-key="id">
                        <template #action-buttons>
                            <h3 class="text-secondary-500 mb-0">
                                Active Assignments
                            </h3>
                        </template>
                        <template slot-scope="props">
                            <d-table-column field="name" :row="props.row" label="Name" />
                            <d-table-column field="dueDateDisplay" :row="props.row" label="Due Date" />
                            <d-table-column class="text-right">
                                <a target="_blank" :href="'/assignments/'+props.row.id"><u>View</u></a>
                            </d-table-column>
                        </template>
                    </d-table>
                    <br />

                    <h3 class="text-secondary-500">
                        Tasks
                    </h3>
                    <hr />
                    <br />
                    <d-table class="mt-4" :data="reportData.nonSubmittedAndOverdueTodos" striped detail-key="id">
                        <template #action-buttons>
                            <h3 class="text-primary-500 mb-0">
                                Non Submitted and Overdue
                            </h3>
                        </template>
                        <template slot-scope="props">
                            <d-table-column field="assignmentName" :row="props.row" label="Assignment Name" />
                            <d-table-column field="todoName" :row="props.row" label="Todo Name" />
                            <d-table-column field="reviewerName" :row="props.row" label="Reviewer" />
                            <d-table-column field="dueDateDisplay" :row="props.row" label="Due Date" />
                            <d-table-column class="text-right">
                                <a target="_blank" :href="'/assignments/todo/'+props.row.id"><u>View</u></a>
                            </d-table-column>
                        </template>
                    </d-table>
                    <br />
                    <br />
                    <d-table class="mt-4" :data="reportData.submitedButNotCompleteTodos" striped detail-key="id">
                        <template #action-buttons>
                            <h3 class="text-primary-500 mb-0">
                                Submitted But Not Completed (Need Review)
                            </h3>
                        </template>
                        <template slot-scope="props">
                            <d-table-column field="assignmentName" :row="props.row" label="Assignment Name" />
                            <d-table-column field="todoName" :row="props.row" label="Task Name" />
                            <d-table-column field="reviewerName" :row="props.row" label="Reviewer" />
                            <d-table-column field="dueDateDisplay" :row="props.row" label="Due Date" />
                            <d-table-column class="text-right">
                                <a target="_blank" :href="'/assignments/todo/'+props.row.id"><u>View</u></a>
                            </d-table-column>
                        </template>
                    </d-table>
                    <br />
                    <br />
                    <d-table class="mt-4" :data="reportData.upcomingTodos" striped detail-key="id">
                        <template #action-buttons>
                            <h3 class="text-primary-500 mb-0">
                                Upcoming
                            </h3>
                        </template>
                        <template slot-scope="props">
                            <d-table-column field="assignmentName" :row="props.row" label="Assignment Name" />
                            <d-table-column field="todoName" :row="props.row" label="Task Name" />
                            <d-table-column field="reviewerName" :row="props.row" label="Reviewer" />
                            <d-table-column field="dueDateDisplay" :row="props.row" label="Due Date" />
                            <d-table-column class="text-right">
                                <a target="_blank" :href="'/assignments/todo/'+props.row.id"><u>View</u></a>
                            </d-table-column>
                        </template>
                    </d-table>
                    <d-table class="mt-4 mb-4" :data="reportData.submittedAndCompletedTodos" striped detail-key="id">
                        <template #action-buttons>
                            <h3 class="text-primary-500 mb-0">
                                Submitted and Completed
                            </h3>
                        </template>
                        <template slot-scope="props">
                            <d-table-column field="assignmentName" :row="props.row" label="Assignment Name" />
                            <d-table-column field="todoName" :row="props.row" label="Task Name" />
                            <d-table-column field="reviewerName" :row="props.row" label="Reviewer" />
                            <d-table-column field="dueDateDisplay" :row="props.row" label="Due Date" />
                            <d-table-column class="text-right">
                                <a target="_blank" :href="'/assignments/todo/'+props.row.id"><u>View</u></a>
                            </d-table-column>
                        </template>
                    </d-table>
                </div>
            </div>
            </div>
    </div>
</template>

<script>
  import axios from 'axios';
    import debounce from 'lodash/debounce';

  export default {
    parent: true,

    data() {
      return {
        fetchingReport: false,
          isFetchingUsers: false,
          isFetchingGroups: false,
          users: [],
          availableGroups: [],
          assigneeNameEmail: '',
          searchGroupValue: '',
          reports: null
      };
    },
    watch: {
    },

    mounted() {
    },
    methods: {
        getUserDataAsync: debounce(async function (name = '') {
            this.isFetchingUsers = true;
            try {
                const { data } = await axios.get(`/api/user-lookup?filter=${name}`);
                this.users = data;
            } catch (error) {
                this.users = [];
                throw error;
            } finally {
                this.isFetchingUsers = false;
            }
        }, 500),

        getGroupsDataAsync: debounce(async function (name = '') {
            try {
                const { data } = await axios.get(`/api/group-lookup?filter=${name}`);
                this.availableGroups = data;
            } catch (error) {
                this.availableGroups = [];
                throw error;
            } finally {
                this.isFetchingGroups = false;
            }
        }, 500),

        attemptGetGroups(name) {
            this.isFetchingGroups = true;
            this.getGroupsDataAsync(name);
        },

        addToGroupArray(groupId) {
            if (groupId) {
                const selectedGroup = this.availableGroups.find((x) => x.id == groupId);

                if (selectedGroup) {
                    if (
                        !this.selectedGroups.find((m) => m.id == selectedGroup.id)
                    ) {
                        this.selectedGroups.push({
                            userId: null,
                            groupId,
                            entityId: this.entityId,
                            name: selectedGroup.name,
                            entityType: this.entityType,
                        });
                    }
                }

                this.searchGroupValue = '';
                this.$nextTick(() => {
                    this.searchGroupValue = null;
                });
            }
        },

      async handleSubmit() {
          const params = [`assigneeNameOrEmail=${this.assigneeNameEmail}`, `groupId=${this.searchGroupValue}`].join('&');
            this.fetchingReport = true;
         try {
             const { data } = await axios.get(`/api/reports/assignment-status?${params}`);
             this.reports = data;
        
        } catch (error) {
             this.$toast.open({ message: error.response.data, color: 'red-500', duration: 7000 });
        } finally {
          this.fetchingReport = false;
        }
      },
    },
  };
</script>